import { IRequirementNpaCount } from '../interfaces/IRequirementNpaCount';
import { getServiceBaseURL } from '../../MainModule/common/getServiceBaseURL';
import { APIServiceEnum } from '../../MainModule/enums/APIServiceEnum';
import { IRequirementNpa } from '../interfaces/IRequirementNpa';
import { IRequirementsNpaFilter } from '../interfaces/IRequirementsNpaFilter';
import { IRequirementStructuralUnit, IRequirementStructuralUnitRaw } from '../interfaces/IRequirementStructuralUnit';
import { IRequirementsMandatory, IRequirementsMandatoryRaw } from '../interfaces/IRequirementsMandatory';
import { ICreateNpaRequest } from '../interfaces/ICreateNpaRequest';
import { handleResponse } from '../../MainModule/common/handleResponse';
import { formatDate } from '../../../CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from '../../../CorePackage/MainModule/constants/dateFormats';
import { requirementStructuralUnitTypes } from '../../../KnmPackage/RequirementsNpaModalModule/components/requirementStructuralUnitTypes';
import { IEditNpaResponse } from '../interfaces/IEditNpaResponse';
import { IRequirementNpaResponse } from '../interfaces/IRequirementNpaResponse';
import {
  IRequirementsMandatoryNew,
  IStructuresUnitsAsTreeResponse,
} from '../interfaces/IStructuresUnitsAsTreeResponse';
import { IGetStructureUnitFlat } from '../interfaces/IGetStructureUnitFlat';
import { IRequirementNpaFlatItem } from '../interfaces/IRequirementNpaFlatItem';
import { IRequirementNpaSuAndMandatoryResponse } from '../interfaces/IRequirementNpaSuAndMandatoryResponse';
import { apiGet } from '../../MainModule/common/apiTools';

function npaFilterToUrlParams(filter: IRequirementsNpaFilter): URLSearchParams {
  const queryParams = new URLSearchParams();

  filter.source && queryParams.append('source', filter.source);
  filter.oivCode && queryParams.append('oivControlId', filter.oivCode);
  filter.supervisionType && queryParams.append('oivControlTypeId', filter.supervisionType);
  filter.query && queryParams.append('query', filter.query);

  return queryParams;
}

export function structuralUnitConverter(su: IRequirementStructuralUnit): IRequirementStructuralUnit {
  const stringComponents: string[] =
    su?.components?.map(component => {
      const type = requirementStructuralUnitTypes.find(item => item.value === component.type);

      return `${type?.label || ''} ${component.value}`;
    }) || [];

  return {
    ...su,
    title: stringComponents.join(', '),
  };
}

function npaConverter<T extends IRequirementNpa>(npa: T): T {
  return {
    ...npa,
    acceptanceDate: formatDate({
      date: npa.acceptanceDate || '',
      dateFormat: DATE_FORMAT_LONG_FNS,
      parseFormat: SYSTEM_DATE_FORMAT_FNS,
    }),
  };
}

export async function fetchNpaCount(filter: IRequirementsNpaFilter): Promise<IRequirementNpaCount> {
  return fetch(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/count?${npaFilterToUrlParams(filter)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  ).then(handleResponse);
}

export async function fetchNpaList(filter: IRequirementsNpaFilter): Promise<IRequirementNpa[]> {
  return fetch(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements?${npaFilterToUrlParams(filter)}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  )
    .then(handleResponse)
    .then((value: IRequirementNpa[]) => value.map(item => npaConverter(item)))
    .then((value: IRequirementNpa[]) =>
      value.map(item => ({
        ...item,
        oivControlId: filter.oivCode,
        oivControlTypeId: filter.supervisionType,
      })),
    );
}

export async function fetchStructuralUnitList(
  npaId: string | number,
  oivControlId: string | null,
  oivControlTypeId: string | null,
): Promise<IRequirementStructuralUnit[]> {
  const queryParams = new URLSearchParams();

  oivControlId && queryParams.append('oivControlId', oivControlId);
  oivControlTypeId && queryParams.append('oivControlTypeId', oivControlTypeId);

  return fetch(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/${npaId}/structural-units?${queryParams}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  )
    .then(handleResponse)
    .then((value: IRequirementStructuralUnit[]) => value.map(unit => structuralUnitConverter(unit)));
}

export async function fetchMandatoryList(
  npaId: string | number,
  suId: string | number,
  filter: IRequirementsNpaFilter,
): Promise<IRequirementsMandatory[]> {
  return fetch(
    `${getServiceBaseURL(
      APIServiceEnum.LK_SERVICE,
    )}/catalogs/requirements/${npaId}/structural-units/${suId}/mandatory-requirements?${npaFilterToUrlParams(filter)}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  ).then(handleResponse);
}

export async function fetchNpaById(id: number): Promise<IRequirementNpa> {
  return fetch(`${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/${id}`, {
    method: 'GET',
    credentials: 'include',
  })
    .then(handleResponse)
    .then((value: IRequirementNpa) => npaConverter(value));
}

export async function fetchStructuralUnitById(npaId: number, suId: number): Promise<IRequirementStructuralUnit> {
  return fetch(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/${npaId}/structural-units/${suId}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  )
    .then(handleResponse)
    .then(value => structuralUnitConverter(value));
}

export async function fetchMandatoryById(npaId: number, suId: number, mId: number): Promise<IRequirementsMandatory> {
  return fetch(
    `${getServiceBaseURL(
      APIServiceEnum.LK_SERVICE,
    )}/catalogs/requirements/${npaId}/structural-units/${suId}/mandatory-requirements/${mId}`,
    {
      method: 'GET',
      credentials: 'include',
    },
  ).then(handleResponse);
}

export async function createNpa(npa: ICreateNpaRequest): Promise<IRequirementNpa> {
  return fetch(`${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(npa),
  })
    .then(handleResponse)
    .then(value => npaConverter(value))
    .then(value => ({
      ...value,
      oivControlId: npa.oivControlId,
      oivControlTypeId: npa.oivControlTypeId,
    }));
}

export async function deleteNpa(npaId: number): Promise<void> {
  return fetch(`${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/${npaId}`, {
    method: 'DELETE',
    credentials: 'include',
  }).then(handleResponse);
}

export async function createStructuralUnitsForNpa(
  npaId: number,
  structuralUnits: IRequirementStructuralUnitRaw[],
): Promise<IRequirementStructuralUnit[]> {
  return fetch(`${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/${npaId}/structural-units`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ units: structuralUnits }),
  })
    .then(handleResponse)
    .then((value: IRequirementStructuralUnit[]) => value.map(unit => structuralUnitConverter(unit)));
}

export async function editNpa(npa: IRequirementNpa): Promise<IEditNpaResponse> {
  return fetch(`${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/${npa.id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(npa),
  })
    .then(handleResponse)
    .then(value => npaConverter(value))
    .then(value => ({ ...value, npaId: value.id, oldNpaId: npa.id }));
}

interface ICreateMandatoryProps {
  mandatory: IRequirementsMandatoryRaw; // Формулировка
  npaId: number;
  suId: number;
}
export async function createMandatory(props: ICreateMandatoryProps): Promise<IRequirementsMandatory> {
  const { mandatory, npaId, suId } = props;

  return fetch(
    `${getServiceBaseURL(
      APIServiceEnum.LK_SERVICE,
    )}/catalogs/requirements/${npaId}/structural-units/${suId}/mandatory-requirements`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mandatory),
    },
  )
    .then(handleResponse)
    .then(value => ({ ...value, structuralUnitId: suId }));
}

export async function fetchNpaListNew({
  knoVersionId,
  supervisionVersionId,
  npaIds,
  mrIds,
}: {
  knoVersionId: string;
  supervisionVersionId: string;
  npaIds?: number[];
  mrIds?: number[];
}): Promise<IRequirementNpaResponse> {
  const queryParams = new URLSearchParams();

  if (npaIds && npaIds.length > 0) {
    npaIds.forEach(id => queryParams.append('npaIds', String(id)));
  }
  if (mrIds && mrIds.length > 0) {
    mrIds.forEach(id => queryParams.append('mrIds', String(id)));
  }

  return fetch(
    `${getServiceBaseURL(
      APIServiceEnum.LK_SERVICE,
    )}/catalogs/requirements/npa?knoVersionId=${knoVersionId}&supervisionVersionId=${supervisionVersionId}&${queryParams.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  ).then(handleResponse);
}

export async function fetchActiveDuplicateNpa(
  npaId: number | undefined,
  knoVersionId: string,
  supervisionVersionId: string,
): Promise<IRequirementNpaResponse> {
  return fetch(
    `${getServiceBaseURL(
      APIServiceEnum.LK_SERVICE,
    )}/catalogs/requirements/active/npa?npaId=${npaId}&knoVersionId=${knoVersionId}&supervisionVersionId=${supervisionVersionId}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  ).then(handleResponse);
}

export async function fetchMandatoryRequirementsListNew({
  npaId,
  suId,
  supervisionVersionId,
  knoVersionId,
  knmCreationDate,
  structuresUnitsFilters,
  mrIds,
}: {
  npaId: number;
  suId: string;
  knoVersionId?: string;
  supervisionVersionId?: string;
  knmCreationDate?: string;
  structuresUnitsFilters?: IStructuresUnitsFilters;
  mrIds?: number[];
}): Promise<IRequirementsMandatoryNew[]> {
  return fetch(
    `${getServiceBaseURL(
      APIServiceEnum.LK_SERVICE,
    )}/catalogs/requirements/${npaId}/structural-unit/mandatory-requirements`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        suId,
        mrIds,
        knoVersionId,
        supervisionVersionId,
        knmCreationDate,
        ...structuresUnitsFilters,
      }),
    },
  ).then(handleResponse);
}

export async function fetchStructuralUnitsByOldFormat(data: IGetStructureUnitFlat): Promise<IRequirementNpaFlatItem[]> {
  return fetch(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/structural-units-requirements-flat`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    },
  ).then(handleResponse);
}

interface ICreateStructuralUnitsRequirementsProps extends IFetchStructuresUnitsFilter {
  units: IRequirementStructuralUnitRaw[];
}
export async function fetchStructuralUnitsRequirements(
  props: ICreateStructuralUnitsRequirementsProps,
): Promise<IRequirementNpaSuAndMandatoryResponse> {
  const { npaId, units } = props;

  return fetch(
    `${getServiceBaseURL(
      APIServiceEnum.LK_SERVICE,
    )}/catalogs/requirements/${npaId}/structural-units-requirements?${makeStructuresUnitsSearchParams(
      props,
    ).toString()}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ units }),
    },
  ).then(handleResponse);
}

export interface IStructuresUnitsFilters {
  objectTypeIds?: string[]; // Идентификаторы типов ОБК из ЕРВК
  objectKindIds?: string[]; // Идентификатор вида ОБК из ЕРВК
  objectSubkindIds?: string[]; // Идентификаторы подвидов ОБК из ЕРВК
  riskIndicatorIds?: string[]; // Идентификаторы индикаторов риска
}

export interface IFetchStructuresUnitsFilter extends IStructuresUnitsFilters {
  npaId?: number; // Идентификатор НПА
  knmCreationDate?: string; // Дата создания паспорта
  knoVersionId?: string; // Идентификатор контрольного (надзорного) органа
  supervisionVersionId?: string; // Идентификатор вида контроля (надзора)
  withRequirements?: boolean; // Добавить привязанные к СЕ формулировки
  suId?: string; // Запрос только по указанной СЕ
  suIds?: number[]; // Получить набор с конкретными СЕ
  mrIds?: number[]; // Получить список включающий конкретные формулировки
}

/**
 * Получение списка структурных единиц в виде дерева
 */
export async function fetchStructuresUnitsAsTree(
  filter: IFetchStructuresUnitsFilter,
): Promise<IStructuresUnitsAsTreeResponse> {
  return fetch(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/${filter.npaId}/structural-units-as-tree`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filter),
    },
  ).then(handleResponse);
}

export async function fetchRequirementsNpaByQuery(query: string): Promise<IRequirementNpa[]> {
  return apiGet(
    `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE, false)}/catalogs/requirements/npa-by-query?query=${query}`,
  );
}

function makeStructuresUnitsSearchParams(filter: IFetchStructuresUnitsFilter): URLSearchParams {
  const {
    knoVersionId,
    supervisionVersionId,
    objectTypeIds,
    objectKindIds,
    objectSubkindIds,
    riskIndicatorIds,
    mrIds,
  } = filter;

  const queryParams = new URLSearchParams();

  knoVersionId && queryParams.append('knoVersionId', knoVersionId);
  supervisionVersionId && queryParams.append('supervisionVersionId', supervisionVersionId);

  if (objectTypeIds && objectTypeIds.length > 0) {
    objectTypeIds.forEach(id => queryParams.append('objectTypeIds', id));
  }

  if (objectKindIds && objectKindIds.length > 0) {
    objectKindIds.forEach(id => queryParams.append('objectKindIds', id));
  }

  if (objectSubkindIds && objectSubkindIds.length > 0) {
    objectSubkindIds.forEach(id => queryParams.append('objectSubkindIds', id));
  }

  if (riskIndicatorIds && riskIndicatorIds.length > 0) {
    riskIndicatorIds.forEach(id => queryParams.append('riskIndicatorIds', id));
  }

  if (mrIds && mrIds.length > 0) {
    mrIds.forEach(id => queryParams.append('mrIds', `${id}`));
  }

  return queryParams;
}

/**
 * Получение всего списка структурных единиц (для кнопки "Выбрать все ОТ")
 */
export async function fetchNpaFull(filter: IFetchStructuresUnitsFilter): Promise<IRequirementNpaFlatItem[]> {
  return fetch(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/npa-full?${makeStructuresUnitsSearchParams(
      filter,
    ).toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  ).then(handleResponse);
}

export interface IChekMandatoryRequirementFilters {
  objectTypeId?: string;
  objectKindId?: string;
  knoVersionId?: string;
  supervisionVersionId?: string;
  objectSubkindId?: string;
  riskIndicatorId?: string[];
  npaRequest?: { mrId: string }[];
}
/**
 * Проверка списка формулировок текущим выбранным фильтрам
 */
export async function chekMandatoryRequirement(
  filters: IChekMandatoryRequirementFilters,
): Promise<{
  mandatoryRequirements: {
    isCorrect: boolean;
    knoVersionIsCorrect: boolean;
    objectKindIsCorrect: boolean;
    objectSubkindIsCorrect: boolean;
    objectTypeIsCorrect: boolean;
    riskIndicatorIsCorrect: boolean;
    supervisionIsCorrect: boolean;
    mandatoryRequirementId: number;
  }[];
}> {
  return fetch(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/requirements/check-mandatory-requirement-with-filter`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filters),
    },
  ).then(handleResponse);
}
